import React, { useContext, useState, useEffect, useCallback } from 'react';
import './App.css';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import AdminChallenges from './components/AdminChallenges';
import { FiMenu, FiX, FiLogOut, FiLoader } from 'react-icons/fi';
import Register from './components/Register';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Profile from './components/Profile';
import UserProfile from './components/UserProfile';
import AdminUsers from './components/AdminUsers';
import AdminReports from './components/AdminReports';
import AdminDashboard from './components/AdminDashboard';
import Home from './components/Home';
import CreateEvent from './components/CreateEvent';
import EventsList from './components/EventsList';
import PersonalStats from './components/PersonalStats';
import MapPage from './components/MapPage';
import ChallengesPage from './components/ChallengesPageNew';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import SplashScreen from './components/SplashScreen';
import { useAuth } from './contexts/AuthContext';
import ToastContext from './contexts/ToastContext';
import Matches from './components/Matches';
import { HelpPage, FAQPage, InfoPage, RankingsPage } from './pages/InfoPages';

function App() {
  const { currentUser, logout, loading: authLoading } = useAuth();
  const toast = useContext(ToastContext);
  
  const navigate = useNavigate();
  const location = useLocation();
  const isPasswordReset = location.pathname === '/reset-password';
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  // Memoize the onComplete callback to prevent infinite useEffect loops in SplashScreen
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
  }, []);

  // Fonction pour vérifier si un lien est actif
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Fonction pour obtenir le style d'un lien actif
  const getNavLinkStyle = (path) => {
    const isCurrentActive = isActive(path);
    return {
      borderBottom: isCurrentActive ? '3px solid var(--accent)' : 'none',
      fontWeight: isCurrentActive ? '600' : '400',
      color: isCurrentActive ? 'var(--accent)' : 'inherit',
      paddingBottom: '0.25rem'
    };
  };

  // (dropdown utilisateur déplacé vers la sidebar)

  async function handleLogout() {
    try {
      await logout();
      if (toast && toast.success) toast.success('Déconnecté');
      navigate('/');
    } catch (err) {
      if (toast && toast.error) toast.error(err.message || 'Erreur lors de la déconnexion');
    }
  }

  // Show loading indicator while auth is initializing
  if (authLoading) {
    return (
      <div className="App" style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <FiLoader size={48} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent)' }} />
          <p style={{ marginTop: '1rem', color: 'var(--muted)' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Splash Screen */}
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-to-main">
        Sauter au contenu principal
      </a>
      
      {!isPasswordReset && <header className="app-header">
        <div className="app-bar container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="brand">
            {/* Put your logo file as public/logo.png (or change the path) */}
            <img src="/logo.png" alt="GoPlay" className="brand-logo" />
            <h1>GoPlay</h1>
          </div>
          
          {/* Menu burger button - Visible only on small screens */}
          <button 
            className="menu-burger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menuOpen}
            title="Menu de navigation"
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text)',
              fontSize: '1.5rem',
              zIndex: 1000
            }}
          >
            {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>

          <nav className={`primary-nav nav-desktop ${menuOpen ? 'nav-mobile-open' : ''}`} aria-label="Primary navigation">
            <div className="nav-left">
              <div className="nav-links" role="navigation">
                <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>Accueil</Link>
                <Link to="/events" className={`nav-link ${isActive('/events') ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>Événements</Link>
              </div>
            </div>

            <div className="nav-actions">
              {!currentUser && (
                <>
                  <Link to="/register" className="btn btn-primary nav-action" onClick={() => setMenuOpen(false)}>Inscription</Link>
                  <Link to="/login" className="btn btn-primary nav-action" onClick={() => setMenuOpen(false)}>Connexion</Link>
                </>
              )}

              {currentUser?.isAdmin && (
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', paddingLeft: '1rem', borderLeft: '1px solid rgba(255,255,255,0.1)' }}>
                  <Link className="btn btn-ghost nav-link-admin" to="/admin" style={getNavLinkStyle('/admin')} onClick={() => setMenuOpen(false)}>Dashboard</Link>
                  <Link className="btn btn-ghost nav-link-admin" to="/admin-users" style={getNavLinkStyle('/admin-users')} onClick={() => setMenuOpen(false)}>Utilisateurs</Link>
                  <Link className="btn btn-ghost nav-link-admin" to="/admin-challenges" style={getNavLinkStyle('/admin-challenges')} onClick={() => setMenuOpen(false)}>Défis admin</Link>
                  <Link className="btn btn-ghost nav-link-admin" to="/admin-reports" style={getNavLinkStyle('/admin-reports')} onClick={() => setMenuOpen(false)}>Signalements</Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      </header>}

      {/* Menu Avatar Utilisateur et Thème - Positionné en haut à droite */}
      {!isPasswordReset && <div style={{
        position: 'fixed',
        top: '1rem',
        right: '1rem',
        zIndex: '1001',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        {/* Notifications, Messages et Profil déplacés vers la Sidebar */}
        
        {/* Bouton déconnexion (remplace le toggle thème) */}
        {currentUser && (
          <button
            onClick={handleLogout}
            aria-label="Se déconnecter"
            title="Se déconnecter"
            style={{
              background: 'linear-gradient(90deg, var(--accent), var(--accent-2))',
              border: 'none',
              cursor: 'pointer',
              color: '#061024',
              fontSize: '1rem',
              padding: '0.5rem 0.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              boxShadow: '0 6px 18px rgba(60,78,90,0.08)'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.96)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
          >
            <FiLogOut size={18} style={{ marginRight: 8 }} /> Déconnexion
          </button>
        )}

        {/* Utilisateur: profil & déconnexion accessibles via la Sidebar */}
      </div>}

      <div className={`app-container${isPasswordReset ? ' password-reset-container' : ''}`}>
        {!isPasswordReset && <Sidebar />}
        <main className="main" id="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/user/:id" element={<UserProfile />} />
            <Route path="/admin" element={
              <ProtectedRoute requireRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin-users" element={
              <ProtectedRoute requireRole="admin">
                <AdminUsers />
              </ProtectedRoute>
            } />
            <Route path="/admin-reports" element={
              <ProtectedRoute requireRole="admin">
                <AdminReports />
              </ProtectedRoute>
            } />
            <Route path="/admin-challenges" element={
              <ProtectedRoute requireRole="admin">
                <AdminChallenges />
              </ProtectedRoute>
            } />
            <Route path="/events" element={<EventsList />} />
            <Route path="/create-event" element={<CreateEvent />} />
            <Route path="/stats" element={<PersonalStats />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/challenges" element={<ChallengesPage />} />
            <Route path="/matches" element={<Matches />} />
            <Route path="/players" element={<InfoPage pageKey="/players" />} />
            <Route path="/friends" element={<InfoPage pageKey="/friends" />} />
            <Route path="/rankings" element={<RankingsPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/contact" element={<InfoPage pageKey="/contact" />} />
            <Route path="/report" element={<InfoPage pageKey="/report" />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<InfoPage pageKey="/terms" />} />
            <Route path="/privacy" element={<InfoPage pageKey="/privacy" />} />
            <Route path="/cookies" element={<InfoPage pageKey="/cookies" />} />
          </Routes>
        </main>
      </div>

      {!isPasswordReset && <Footer />}

      {/* Toasts container (JS can append .toast nodes here) */}
      <div className="toasts" id="toasts" />

      {/* Floating action button removed; 'Créer' accessible via Sidebar only */}
    </div>
  );
}

export default App;
